import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Calendar, Plus, Minus, Search, ChevronDown, ArrowLeft } from 'lucide-react';
import { Button } from './button';
import { Input } from './input';
import { useNoshStore } from '../../lib/store';
import { getUnitValues } from '../../lib/measurement-units';

// Helper function to fix floating point precision issues with decimals
const roundToTwoDecimals = (num: number): number => {
  return Math.round((num + Number.EPSILON) * 100) / 100;
};

// Helper function to safely adjust quantity with proper decimal precision
const adjustQuantity = (currentQuantity: number, adjustment: number): number => {
  const result = currentQuantity + adjustment;
  return Math.max(0.01, roundToTwoDecimals(result)); // Minimum 0.01, rounded to 2 decimals
};

// Smart food categorization logic
const categorizeFood = (name: string): { category: string; location: 'fridge' | 'pantry' | 'freezer'; emoji: string } => {
  const nameLower = name.toLowerCase().trim();
  
  // Seafood patterns (separate from meat)
  if (nameLower.match(/\b(tuna|salmon|fish|cod|sardine|mackerel|trout|bass|tilapia|seafood)\b/) ||
      nameLower.match(/\b(shrimp|crab|lobster|oyster|mussel|clam|scallop|squid|octopus)\b/)) {
    const emoji = nameLower.includes('fish') || nameLower.includes('tuna') || nameLower.includes('salmon') ? '🐟' : 
                  nameLower.includes('shrimp') || nameLower.includes('crab') ? '🦐' : '🐟';
    return { category: 'Seafood', location: 'fridge', emoji };
  }
  
  // Meat patterns (excluding seafood)
  if (nameLower.match(/\b(chicken|beef|pork|turkey|duck|lamb|bacon|sausage|ham|meat|steak|ground|mince)\b/)) {
    const emoji = nameLower.includes('chicken') ? '🍗' : 
                  nameLower.includes('beef') || nameLower.includes('steak') ? '🥩' : 
                  nameLower.includes('bacon') ? '🥓' : '🍖';
    return { category: 'Meat', location: 'fridge', emoji };
  }
  
  // Dairy patterns
  if (nameLower.match(/\b(milk|cheese|butter|cream|yogurt|cottage|cheddar|mozzarella|parmesan|dairy)\b/) ||
      nameLower.match(/\b(egg|eggs)\b/)) {
    const emoji = nameLower.includes('milk') ? '🥛' : 
                  nameLower.includes('cheese') ? '🧀' : 
                  nameLower.includes('butter') ? '🧈' : 
                  nameLower.includes('egg') ? '🥚' : '🥛';
    return { category: 'Dairy', location: 'fridge', emoji };
  }
  
  // Fruits patterns
  if (nameLower.match(/\b(apple|banana|orange|grape|berry|cherry|peach|pear|plum|kiwi|mango|pineapple)\b/) ||
      nameLower.match(/\b(strawberry|blueberry|raspberry|blackberry|lemon|lime|grapefruit|avocado|melon|watermelon)\b/) ||
      nameLower.match(/\b(fruit|berries)\b/)) {
    const emoji = nameLower.includes('apple') ? '🍎' : 
                  nameLower.includes('banana') ? '🍌' : 
                  nameLower.includes('orange') ? '🍊' : 
                  nameLower.includes('grape') ? '🍇' : 
                  nameLower.includes('berry') ? '🍓' : 
                  nameLower.includes('lemon') ? '🍋' : 
                  nameLower.includes('avocado') ? '🥑' : '🍎';
    const location = nameLower.includes('banana') || nameLower.includes('avocado') ? 'pantry' : 'fridge';
    return { category: 'Fruits', location, emoji };
  }
  
  // Vegetables patterns
  if (nameLower.match(/\b(broccoli|carrot|tomato|lettuce|spinach|cabbage|onion|garlic|potato|pepper)\b/) ||
      nameLower.match(/\b(cucumber|celery|radish|beet|turnip|parsnip|corn|peas|beans|vegetable|veggie)\b/) ||
      nameLower.match(/\b(mushroom|zucchini|squash|eggplant|asparagus|artichoke)\b/)) {
    const emoji = nameLower.includes('broccoli') ? '🥦' : 
                  nameLower.includes('carrot') ? '🥕' : 
                  nameLower.includes('tomato') ? '🍅' : 
                  nameLower.includes('lettuce') || nameLower.includes('spinach') ? '🥬' : 
                  nameLower.includes('potato') ? '🥔' : 
                  nameLower.includes('onion') ? '🧅' : 
                  nameLower.includes('corn') ? '🌽' : '🥬';
    const location = nameLower.includes('potato') || nameLower.includes('onion') || nameLower.includes('garlic') ? 'pantry' : 'fridge';
    return { category: 'Vegetables', location, emoji };
  }
  
  // Grains & Bread patterns (including legumes and pasta)
  if (nameLower.match(/\b(bread|rice|pasta|noodle|cereal|oat|wheat|flour|quinoa|barley)\b/) ||
      nameLower.match(/\b(grain|roll|bagel|muffin|cracker|biscuit|bean|lentil|chickpea|legume|pea)\b/)) {
    const emoji = nameLower.includes('bread') ? '🍞' : 
                  nameLower.includes('rice') ? '🍚' : 
                  nameLower.includes('pasta') ? '🍝' : 
                  nameLower.includes('cereal') ? '🥣' : 
                  nameLower.includes('bean') || nameLower.includes('lentil') ? '🫘' : '🌾';
    return { category: 'Grains', location: 'pantry', emoji };
  }
  
  // Nuts & Seeds patterns
  if (nameLower.match(/\b(nut|almond|peanut|walnut|cashew|pecan|pistachio|seed|sunflower|pumpkin)\b/)) {
    const emoji = '🥜';
    return { category: 'Nuts & Seeds', location: 'pantry', emoji };
  }
  
  // Default fallback for everything else (beverages, condiments, sweeteners, frozen, etc.)
  return { category: 'Other', location: 'pantry', emoji: '📦' };
};

// Common emojis for quick selection
const commonEmojis = [
  "🍎", "🍌", "🥦", "🍗", "🧀", "🥚", "🍞", "🥛", "🍚", "🍝", 
  "🍕", "🥩", "🍔", "🍜", "🍣", "🥗", "🍲", "🥤", "🧊", "🍓", 
  "🍇", "🍊", "🍋", "🍉", "🥕", "🍅", "🥔", "🌽", "🍑", "🍐",
  "🐟", "🦐", "🥓", "🧈", "🥜", "🍫", "🧂", "🍯", "☕", "🍵"
];

// Database-matching categories based on sample data
const categories = [
  'Meat',
  'Seafood',
  'Dairy',
  'Vegetables',
  'Fruits',
  'Grains',
  'Nuts & Seeds',
  'Other'
];

interface AddItemModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const AddItemModal: React.FC<AddItemModalProps> = ({ isOpen, onClose }) => {
  const { addItem, user } = useNoshStore();
  
  // Get unit options based on user's measurement system preference
  const unitOptions = getUnitValues(user.measurementSystem || 'metric');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [emojiSearch, setEmojiSearch] = useState('');
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    category: '',
    location: 'pantry' as 'fridge' | 'pantry' | 'freezer',
    quantity: 1 as number | string,
    unit: 'pieces',
    expiryDate: '',
    emoji: '📦'
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Hide mobile navigation when modal is open
  useEffect(() => {
    if (isOpen) {
      // Store original values
      const originalStyle = window.getComputedStyle(document.body).overflow;
      const originalPosition = document.body.style.position;
      const originalTop = document.body.style.top;
      const scrollY = window.scrollY;
      
      // Lock body scrolling completely
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.height = '100%';
      
      // Prevent touch scrolling on mobile
      document.body.style.touchAction = 'none';
      document.documentElement.style.overflow = 'hidden';
      
      if (isMobile) {
        document.body.classList.add('modal-open');
      }
      
      return () => {
        // Restore everything
        document.body.style.overflow = originalStyle;
        document.body.style.position = originalPosition;
        document.body.style.top = originalTop;
        document.body.style.width = '';
        document.body.style.height = '';
        document.body.style.touchAction = '';
        document.documentElement.style.overflow = '';
        document.body.classList.remove('modal-open');
        
        // Restore scroll position
        window.scrollTo(0, scrollY);
      };
    } else {
      document.body.classList.remove('modal-open');
    }
  }, [isOpen, isMobile]);

  // Smart auto-detection based on item name
  useEffect(() => {
    if (formData.name.trim()) {
      const detected = categorizeFood(formData.name);
      setFormData(prev => ({
        ...prev,
        emoji: detected.emoji,
        category: detected.category,
        location: detected.location
      }));
    } else {
      // Reset to defaults when name is empty
      setFormData(prev => ({
        ...prev,
        emoji: '📦',
        category: '',
        location: 'pantry'
      }));
    }
  }, [formData.name]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.category) newErrors.category = 'Category is required';
    if (!formData.location) newErrors.location = 'Location is required';
    const numQuantity = typeof formData.quantity === 'string' ? parseFloat(formData.quantity) : formData.quantity;
    if (isNaN(numQuantity) || numQuantity <= 0 || numQuantity > 9999999.999) {
      newErrors.quantity = 'Quantity must be between 0.001 and 9,999,999.999';
    }
    if (!formData.unit) newErrors.unit = 'Unit is required';
    if (!formData.expiryDate) newErrors.expiryDate = 'Expiry date is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    try {
      const finalQuantity = typeof formData.quantity === 'string' ? parseFloat(formData.quantity) || 1 : formData.quantity;
      await addItem({
        name: formData.name,
        category: formData.category,
        location: formData.location,
        quantity: finalQuantity,
        unit: formData.unit,
        expiryDate: formData.expiryDate,
        emoji: formData.emoji
      });
      
      // Reset form and close modal
      setFormData({
        name: '', category: '', location: 'pantry', quantity: 1, 
        unit: 'pieces', expiryDate: '', emoji: '📦'
      });
      setErrors({});
      onClose();
    } catch (error) {
      console.error('Failed to add item:', error);
    }
  };

  const filteredEmojis = emojiSearch 
    ? commonEmojis.filter(emoji => {
        // Simple emoji search - could be enhanced
        const emojiMap: Record<string, string[]> = {
          '🍎': ['apple', 'fruit'],
          '🍌': ['banana'],
          '🥦': ['broccoli', 'vegetable'],
          '🍗': ['chicken', 'meat'],
          '🧀': ['cheese', 'dairy'],
          '🥚': ['egg'],
          '🐟': ['fish', 'tuna', 'salmon'],
          '🥩': ['beef', 'meat', 'steak']
        };
        const keywords = emojiMap[emoji] || [];
        return keywords.some(keyword => keyword.includes(emojiSearch.toLowerCase()));
      })
    : commonEmojis;

  const today = new Date().toISOString().split('T')[0];

  if (!isOpen) return null;

  const modalContent = (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Name and Emoji Row */}
      <div className="flex gap-4">
        <div className="flex-1">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Item Name
          </label>
          <Input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
            placeholder="e.g. Fresh Tuna"
            className={`h-11 ${errors.name ? 'border-red-500' : ''}`}
          />
          {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Icon
          </label>
          <div className="relative">
            <button
              type="button"
              onClick={() => setShowEmojiPicker(!showEmojiPicker)}
              className="w-11 h-11 text-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors flex items-center justify-center"
            >
              {formData.emoji}
            </button>
            
            {showEmojiPicker && (
              <div className="absolute top-12 right-0 z-50 w-72 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-xl shadow-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Search size={16} className="text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search emoji..."
                    value={emojiSearch}
                    onChange={(e) => setEmojiSearch(e.target.value)}
                    className="flex-1 px-3 py-1.5 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-transparent"
                  />
                </div>
                <div className="grid grid-cols-8 gap-1 max-h-32 overflow-y-auto">
                  {filteredEmojis.map((emoji, index) => (
                    <button
                      key={index}
                      type="button"
                      onClick={() => {
                        setFormData(prev => ({ ...prev, emoji }));
                        setShowEmojiPicker(false);
                      }}
                      className="w-8 h-8 text-lg hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors flex items-center justify-center"
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Category Dropdown */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Category
        </label>
        <div className="relative">
          <select
            value={formData.category}
            onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
            className={`w-full h-11 px-3 pr-10 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-gray-100 appearance-none ${
              errors.category ? 'border-red-500' : ''
            }`}
          >
            <option value="">Select category...</option>
            {categories.map((category) => (
              <option key={category} value={category}>
                {category}
              </option>
            ))}
          </select>
          <ChevronDown size={16} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" />
        </div>
        {errors.category && <p className="text-red-500 text-xs mt-1">{errors.category}</p>}
      </div>

      {/* Storage Location - Smaller buttons */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
          Storage Location
        </label>
        <div className="grid grid-cols-3 gap-2">
          {(['fridge', 'pantry', 'freezer'] as const).map((location) => (
            <button
              key={location}
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, location }))}
              className={`px-3 py-2 text-sm font-medium rounded-lg transition-all flex flex-col items-center gap-1 ${
                formData.location === location
                  ? 'bg-primary text-primary-foreground shadow-md scale-105'
                  : 'bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              <span className="text-lg">
                {location === 'fridge' ? '❄️' : location === 'pantry' ? '🏠' : '🧊'}
              </span>
              <span className="text-xs">{location.charAt(0).toUpperCase() + location.slice(1)}</span>
            </button>
          ))}
        </div>
        {errors.location && <p className="text-red-500 text-xs mt-1">{errors.location}</p>}
      </div>

      {/* Quantity and Unit */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Quantity
          </label>
          <div className="flex items-center bg-gray-50 dark:bg-gray-800 rounded-xl p-1">
            <button
              type="button"
              onClick={() => {
                const currentQuantity = typeof formData.quantity === 'string' ? parseFloat(formData.quantity) || 1 : formData.quantity;
                const newQuantity = Math.max(0.001, currentQuantity - (currentQuantity >= 1 ? 1 : 0.1));
                setFormData(prev => ({ ...prev, quantity: roundToTwoDecimals(newQuantity) }));
              }}
              className="p-2 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors flex-shrink-0"
            >
              <Minus size={16} />
            </button>
            <input
              type="number"
              value={formData.quantity || ''}
              onChange={(e) => {
                const value = e.target.value;
                if (value === '') {
                  // Allow empty value during typing
                  setFormData(prev => ({ ...prev, quantity: '' }));
                } else {
                  const numValue = parseFloat(value);
                  if (!isNaN(numValue) && numValue > 0) {
                    setFormData(prev => ({ ...prev, quantity: numValue }));
                  }
                }
              }}
              onBlur={(e) => {
                // Ensure valid value when input loses focus
                const numQuantity = typeof formData.quantity === 'string' ? parseFloat(formData.quantity) : formData.quantity;
                if (!formData.quantity || isNaN(numQuantity) || numQuantity <= 0) {
                  setFormData(prev => ({ ...prev, quantity: 1 }));
                }
              }}
              className="flex-1 px-3 py-2 text-center bg-transparent border-none outline-none min-w-0"
              min="0.001"
              step="any"
              placeholder="1"
            />
            <button
              type="button"
              onClick={() => {
                const currentQuantity = typeof formData.quantity === 'string' ? parseFloat(formData.quantity) || 1 : formData.quantity;
                const newQuantity = currentQuantity + (currentQuantity >= 1 ? 1 : 0.1);
                setFormData(prev => ({ ...prev, quantity: roundToTwoDecimals(newQuantity) }));
              }}
              className="p-2 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors flex-shrink-0"
            >
              <Plus size={16} />
            </button>
          </div>
          {errors.quantity && <p className="text-red-500 text-xs mt-1">{errors.quantity}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Unit
          </label>
          <select
            value={formData.unit}
            onChange={(e) => setFormData(prev => ({ ...prev, unit: e.target.value }))}
            className={`w-full h-11 px-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-gray-100 ${
              errors.unit ? 'border-red-500' : ''
            }`}
          >
            {unitOptions.map((unit: string) => (
              <option key={unit} value={unit}>{unit}</option>
            ))}
          </select>
          {errors.unit && <p className="text-red-500 text-xs mt-1">{errors.unit}</p>}
        </div>
      </div>

      {/* Expiry Date */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Expiry Date
        </label>
        <div className="relative">
          <input
            type="date"
            value={formData.expiryDate}
            onChange={(e) => setFormData(prev => ({ ...prev, expiryDate: e.target.value }))}
            min={today}
            className={`w-full h-11 px-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-gray-100 ${
              errors.expiryDate ? 'border-red-500' : ''
            }`}
          />
          <Calendar size={16} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" />
        </div>
        {errors.expiryDate && <p className="text-red-500 text-xs mt-1">{errors.expiryDate}</p>}
      </div>

      {/* Action Buttons */}
      <div className="flex gap-3 pt-4">
        <Button 
          type="button" 
          variant="outline" 
          onClick={onClose}
          className="flex-1 h-11 rounded-xl"
        >
          Cancel
        </Button>
        <Button 
          type="submit"
          className="flex-1 h-11 rounded-xl"
        >
          Add Item
        </Button>
      </div>
    </form>
  );

  const modalJSX = (
    <AnimatePresence mode="wait">
      {isOpen && (
        <>
          {/* Backdrop - Fixed and covering entire viewport */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25, ease: "linear" }}
            onClick={onClose}
            style={{ 
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              width: '100vw',
              height: '100vh',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              backdropFilter: 'blur(4px)',
              zIndex: 9998,
              margin: 0, 
              padding: 0 
            }}
          />

          {/* Modal */}
          {isMobile ? (
            // Mobile: Slide up from bottom - Truly fixed to viewport
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ 
                duration: 0.3,
                ease: "linear"
              }}
              style={{
                position: 'fixed',
                bottom: 0,
                left: 0,
                right: 0,
                top: 'auto',
                width: '100vw',
                maxHeight: '90vh',
                backgroundColor: 'white',
                borderTopLeftRadius: '24px',
                borderTopRightRadius: '24px',
                zIndex: 9999,
                overflow: 'auto',
                margin: 0,
                padding: 0
              }}
              className="dark:bg-gray-900"
            >
              <div className="h-full flex flex-col">
                {/* Mobile Header */}
                <div className="sticky top-0 z-10 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-center">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    Add Item
                  </h2>
                </div>
                
                {/* Mobile Content */}
                <div className="flex-1 overflow-y-auto p-6">
                  {modalContent}
                </div>
              </div>
            </motion.div>
          ) : (
            // Desktop: Center modal - Fixed positioning
            <div 
              style={{ 
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                width: '100vw',
                height: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 9999,
                margin: 0, 
                padding: '0 1rem' 
              }}
              onClick={onClose}
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.9, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.9, y: 20 }}
                transition={{ 
                  duration: 0.25,
                  ease: "linear"
                }}
                className="bg-white/90 dark:bg-gray-900/90 backdrop-blur-xl rounded-2xl border border-gray-200/50 dark:border-gray-700/50 shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="p-6">
                  {modalContent}
                </div>
              </motion.div>
            </div>
          )}
        </>
      )}
    </AnimatePresence>
  );

  return ReactDOM.createPortal(modalJSX, document.body);
}; 